import { useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import { usePrevious } from 'react-use';

import { useFormContext } from '@/components/react-hook-form';
import { DispositiveProvisionsFormRecipient } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import { isNonTaxable } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsFormRecipients/hooks/useDefaultBearsTax';
import { calculateResiduaryRecipients } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/components/formBody/primaryClientDeathSection/primaryClientDeathSection.utils';
import { PathsOf } from '@/types/subform';

import {
  BENEFICIARY_DESIGNATIONS_FORM_NAMESPACE,
  BeneficiaryDesignationsFormShape,
} from '../BeneficiaryDesignationsForm.types';

/**
 * BDG analog of the DP form's `useResiduaryPaysAllTaxes`. The DP flag lives on
 * the `DispositionScenario` per death event; BDG's equivalent bucket is the
 * per-tier `BeneficiaryDesignationGroup`, so the flag is read off the tier.
 */
export function useBdgResiduaryPaysAllTaxes(tier: 'primary' | 'contingent'): {
  residuaryPaysAllTaxes: boolean;
  residuaryPaysAllTaxesFieldName: PathsOf<BeneficiaryDesignationsFormShape>;
} {
  const { control } = useFormContext<BeneficiaryDesignationsFormShape>();

  const residuaryPaysAllTaxesFieldName =
    `${BENEFICIARY_DESIGNATIONS_FORM_NAMESPACE}.${tier}.residuaryPaysAllTaxes` as const satisfies PathsOf<BeneficiaryDesignationsFormShape>;

  const residuaryPaysAllTaxes = useWatch({
    control,
    name: residuaryPaysAllTaxesFieldName,
  });

  return { residuaryPaysAllTaxes, residuaryPaysAllTaxesFieldName };
}

/**
 * BDG analog of the DP form's `useToggleResiduaryPaysAllTaxes`. Watches the
 * per-tier `residuaryPaysAllTaxes` flag and materializes each recipient's
 * `bearsTax` when it flips:
 * - toggled ON: the residuary recipient(s) bear tax, the rest don't.
 * - toggled OFF: each recipient defaults from its recipient type (taxable bears
 *   tax, charitable / marital do not), then the now-visible per-recipient
 *   checkboxes let the user override.
 *
 * Unlike the DP hook, the BDG "source" is always a retirement/insurance account
 * (never a charitable entity or non-primary individual), so the toggled-OFF
 * default reads each recipient's transfer tax kind rather than the source's
 * `isNonTaxableSource`.
 */
export function useToggleBdgResiduaryPaysAllTaxes({
  tier,
}: {
  tier: 'primary' | 'contingent';
}) {
  const { control, setValue } =
    useFormContext<BeneficiaryDesignationsFormShape>();

  // useWatch (not useFieldArray.fields) so the toggle reads live per-recipient
  // edits — useFieldArray.fields does not reflect individual field changes, so
  // calculateResiduaryRecipients would otherwise see stale kinds/percentages.
  const recipients: DispositiveProvisionsFormRecipient[] | undefined = useWatch(
    {
      control,
      name: `${BENEFICIARY_DESIGNATIONS_FORM_NAMESPACE}.${tier}.recipients`,
    }
  );

  const { residuaryPaysAllTaxes } = useBdgResiduaryPaysAllTaxes(tier);
  const previousResiduaryPaysAllTaxes = usePrevious(residuaryPaysAllTaxes);

  useEffect(() => {
    if (!recipients) return;

    if (previousResiduaryPaysAllTaxes === true && !residuaryPaysAllTaxes) {
      // Toggled OFF: default each recipient's bearsTax from its recipient type.
      // The per-recipient checkbox is now visible, so the user can override.
      // setValue (not useFieldArray.update) so each nested Controller re-renders.
      recipients.forEach((recipient, index) => {
        setValue(
          `${BENEFICIARY_DESIGNATIONS_FORM_NAMESPACE}.${tier}.recipients.${index}.bearsTax`,
          !isNonTaxable(recipient.transferTaxKind),
          { shouldDirty: true }
        );
      });
      return;
    }

    if (previousResiduaryPaysAllTaxes === false && residuaryPaysAllTaxes) {
      // Toggled ON: the residuary recipient(s) bear tax, everyone else does not.
      const { residuaryIndices } = calculateResiduaryRecipients(recipients);
      recipients.forEach((_recipient, index) => {
        setValue(
          `${BENEFICIARY_DESIGNATIONS_FORM_NAMESPACE}.${tier}.recipients.${index}.bearsTax`,
          residuaryIndices.includes(index),
          { shouldDirty: true }
        );
      });
    }
  }, [
    previousResiduaryPaysAllTaxes,
    recipients,
    residuaryPaysAllTaxes,
    setValue,
    tier,
  ]);
}
